import { useMemo } from 'react';
import { useStableMobileDetection } from './useStableMobileDetection';

interface UseDialogSizingOptions {
  excludeHeaderFooter?: boolean;
  enableDebug?: boolean;
}

interface DialogSizingResult {
  collapsedStyle: React.CSSProperties;
  expandedStyle: React.CSSProperties;
  collapsedClass: string;
  expandedClass: string;
  collapsedHeight: number;
  expandedHeight: number;
  gapHeight: number;
  getPositionStyle: (size: 'bottom-sheet' | 'fullscreen' | 'centered-modal', isExpanded?: boolean) => React.CSSProperties;
  debugInfo?: {
    viewport: any;
    headerFooterHeight: number;
    availableHeight: number;
    collapsedHeight: number;
    expandedHeight: number;
    gapHeight: number;
    collapsedCoveragePercent: number;
    expandedCoveragePercent: number;
  };
}

// Use CSS custom properties for dynamic header/footer heights
const getHeaderFooterHeight = () => {
  const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--hdr') || '44');
  const footerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--ftr') || '44');
  return headerHeight + footerHeight;
};

const getPageFooterHeight = () => {
  return parseInt(getComputedStyle(document.documentElement).getPropertyValue('--ftr') || '44');
};

const MOBILE_COLLAPSED_COVERAGE = 0.6; // 60% screen coverage for collapsed state
const MOBILE_EXPANDED_COVERAGE = 0.8; // 80% for expanded
const DESKTOP_COLLAPSED_COVERAGE = 0.6;
const DESKTOP_EXPANDED_COVERAGE = 0.9;

export function useDialogSizing({ 
  excludeHeaderFooter = true, 
  enableDebug = false 
}: UseDialogSizingOptions = {}): DialogSizingResult {
  const viewport = useStableMobileDetection();

  const heights = useMemo(() => {
    const useFullViewport = viewport.isMobile;
    const baseHeight = useFullViewport ? viewport.viewportHeight : viewport.availableHeight;
    const fixedHeight = (excludeHeaderFooter && !useFullViewport) ? getHeaderFooterHeight() : 0;
    const safeAreaHeight = (useFullViewport ? 0 : viewport.safeAreas.top + viewport.safeAreas.bottom);
    const workingHeight = baseHeight - fixedHeight - safeAreaHeight;
    const minHeights = { 
      collapsed: Math.floor(workingHeight * 0.2), // Lower minimum for collapsed (was 0.3)
      expanded: Math.floor(workingHeight * 0.3)   // Lower minimum for expanded (was 0.5 which was causing 50% issue)
    };
    let collapsedCoverage = viewport.isMobile ? MOBILE_COLLAPSED_COVERAGE : DESKTOP_COLLAPSED_COVERAGE;
    let expandedCoverage = viewport.isMobile ? MOBILE_EXPANDED_COVERAGE : DESKTOP_EXPANDED_COVERAGE;
    const collapsed = Math.max(
      Math.floor(workingHeight * collapsedCoverage), 
      minHeights.collapsed
    );
    const expanded = Math.max(
      Math.floor(workingHeight * expandedCoverage), 
      minHeights.expanded
    );
    const gapHeight = Math.max(0, workingHeight - collapsed);

    if (enableDebug) {
      console.log('[useDialogSizing] Device-aware height calculations:', {
        viewport: {
          isMobile: viewport.isMobile,
          orientation: viewport.orientation,
          isIOS: viewport.isIOS,
          isAndroid: viewport.isAndroid,
          viewportHeight: viewport.viewportHeight,
          availableHeight: viewport.availableHeight,
          safeAreas: viewport.safeAreas
        },
        calculations: {
          baseHeight,
          fixedHeight,
          safeAreaHeight,
          workingHeight,
          collapsedCoverage: collapsedCoverage * 100,
          expandedCoverage: expandedCoverage * 100,
          collapsed,
          expanded,
          gapHeight,
          minHeights
        }
      });
    }

    return {
      collapsed,
      expanded,
      available: workingHeight,
      gap: gapHeight
    };
  }, [viewport, excludeHeaderFooter, enableDebug]);

  // Use the calculated percentage heights - 80% collapsed, 100% expanded
  const collapsedStyle = useMemo((): React.CSSProperties => ({
    height: `${heights.collapsed}px`,
    overflowY: 'auto' as const,
  }), [heights.collapsed]);

  const expandedStyle = useMemo((): React.CSSProperties => ({
    height: `${heights.expanded}px`, 
    overflowY: 'auto' as const,
  }), [heights.expanded]);

  const collapsedClass = ''; // Remove artificial height classes
  const expandedClass = ''; // Remove artificial height classes

  const getPositionStyle = useMemo(() => {
    return (size: 'bottom-sheet' | 'fullscreen' | 'centered-modal', isExpanded = false): React.CSSProperties => {
      if (size === 'bottom-sheet') {
        let bottomValue: string;
        if (viewport.isMobile) {
          bottomValue = `${viewport.safeAreas.bottom + getPageFooterHeight()}px`;
        } else {
          bottomValue = excludeHeaderFooter ? `var(--ftr)` : '0px';
        }
        return {
          left: 0,
          right: 0,
          bottom: bottomValue,
          top: 'auto',
          position: 'fixed' as const
        };
      }
      if (size === 'centered-modal') {
        // Desktop constraints!
        const maxWidth = viewport.isMobile ? '100%' : 'min(600px, 90vw)';
        return {
          position: 'fixed' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: viewport.isMobile ? '100%' : 'auto',
          maxWidth,
          minWidth: viewport.isMobile ? '100%' : '400px',
          // Remove artificial height - let content determine size naturally
          maxHeight: 'calc(100vh - 48px)',
        };
      }
      // Fullscreen (used by some setups)
      let topInset: string;
      let bottomInset: string;
      if (excludeHeaderFooter) {
        topInset = viewport.isMobile 
          ? `calc(var(--hdr) + ${viewport.safeAreas.top}px)` 
          : 'var(--hdr)';
        bottomInset = viewport.isMobile 
          ? `calc(var(--ftr) + ${viewport.safeAreas.bottom}px)` 
          : 'var(--ftr)';
      } else {
        topInset = viewport.isMobile ? `${viewport.safeAreas.top}px` : '0px';
        bottomInset = viewport.isMobile ? `${viewport.safeAreas.bottom}px` : '0px';
      }
      return {
        left: 0,
        right: 0,
        top: topInset,
        bottom: bottomInset,
        position: 'fixed' as const
      };
    };
  }, [viewport, excludeHeaderFooter, heights.collapsed, heights.expanded]);

  const debugInfo = enableDebug ? {
    viewport,
    headerFooterHeight: excludeHeaderFooter ? getHeaderFooterHeight() : 0,
    availableHeight: heights.available,
    collapsedHeight: heights.collapsed,
    expandedHeight: heights.expanded,
    gapHeight: heights.gap,
    collapsedCoveragePercent: viewport.isMobile ? MOBILE_COLLAPSED_COVERAGE * 100 : DESKTOP_COLLAPSED_COVERAGE * 100,
    expandedCoveragePercent: viewport.isMobile ? MOBILE_EXPANDED_COVERAGE * 100 : DESKTOP_EXPANDED_COVERAGE * 100,
  } : undefined;

  return {
    collapsedStyle,
    expandedStyle,
    collapsedClass,
    expandedClass,
    collapsedHeight: heights.collapsed,
    expandedHeight: heights.expanded,
    gapHeight: heights.gap,
    getPositionStyle,
    debugInfo
  };
}
